import classNames from 'classnames';
import cn from 'classnames';
import React, { useRef } from 'react';
import { useDispatch } from 'react-redux';
import { Column, useSortBy, useTable } from 'react-table';

import { Typography } from '@sravni/react-design-system';

import { FILTERS_NAME, SORT_BY_PARAM } from '@constants/filters';
import { sendGoToCardEvent } from '@helpers/analyticsEvents';
import type { ICellItem } from '@interfaces/ratingTable';
import { useFilters } from '@store/hooks/filters';
import { setFilters } from '@store/reducers/filters';

import type { TCellFormat } from '../../../../types/ratingTable';
import { IRatingsNormalized, TableSortColumnsMap } from '../../index';

import { Cell } from './Cell';
import styles from './table.module.scss';

interface ITableProps {
  data: IRatingsNormalized[];
  columns: Column<IRatingsNormalized>[];
}

const handleRowClick = (item: ICellItem) => () => {
  const { organizationType, name, companyUrl } = item;
  sendGoToCardEvent({ organizationType, name });
  window.open(companyUrl, '_blank', 'noreferrer');
};

const renderDataCell = (cell) => {
  return (
    <Cell {...cell.getCellProps()} cellFormat={cell.column.id} item={cell.row.original}>
      {cell.render('Cell')}
    </Cell>
  );
};

export const Table = React.memo<ITableProps>(({ data, columns }) => {
  const filters = useFilters();
  const dispatch = useDispatch();
  const firstRender = useRef<boolean>(true);

  let initialStateSortBy = { sortBy: [] };
  if (firstRender.current) {
    firstRender.current = false;

    const querySortBy = filters[FILTERS_NAME.SORT_BY];
    const columnId = TableSortColumnsMap[querySortBy];

    const isAscSorting = filters[FILTERS_NAME.IS_ASC_SORTING];

    initialStateSortBy = {
      sortBy: [{ id: columnId, desc: !isAscSorting }],
    };
  }

  const handleHeaderClick = (column) => () => {
    if (!column.canSort) return;

    const columnId = column.id;
    const sortBy = Object.entries(TableSortColumnsMap).find(([_, value]) => value === columnId)[0];
    const isDescSorting = column.isSortedDesc ?? false;

    dispatch(
      setFilters({
        ...filters,
        [FILTERS_NAME.SORT_BY]: sortBy as SORT_BY_PARAM,
        [FILTERS_NAME.IS_ASC_SORTING]: isDescSorting,
      }),
    );

    column.toggleSortBy();
  };

  const tableInstance = useTable(
    {
      columns,
      data,
      disableMultiSort: true, // Disables multi-sorting for the entire table.
      disableSortRemove: true, // the un-sorted state will not be available to columns once they have been sorted
      initialState: initialStateSortBy,
      autoResetSortBy: false, // the sortBy state will not automatically reset if any of the following conditions are met: data is changed
    },
    useSortBy,
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = tableInstance;

  return (
    <div className={cn(styles.tableWrapper, styles.scrollable)}>
      <table aria-label={'Рейтинг'} className={styles.table} {...getTableProps()}>
        <thead className={classNames(styles.headers)}>
          {headerGroups.map((headerGroup) => {
            return (
              <tr {...headerGroup.getHeaderGroupProps()} className={styles.row}>
                {headerGroup.headers.map((column) => {
                  return (
                    <th {...column.getHeaderProps(column.getSortByToggleProps())} onClick={handleHeaderClick(column)}>
                      <Typography.Text size={14} strong={!column.isSorted}>
                        {column.render('Header')}
                      </Typography.Text>
                    </th>
                  );
                })}
              </tr>
            );
          })}
        </thead>
        <tbody {...getTableBodyProps()}>
          {rows.map((row, i) => {
            prepareRow(row);
            const numberCell = row.cells[0];
            const nameCell = row.cells[1];
            const restColumns = row.cells.slice(2);

            return (
              <tr onClick={handleRowClick(nameCell.row.original as ICellItem)} {...row.getRowProps()}>
                <Cell {...numberCell.getCellProps()} cellFormat={numberCell.column.id as TCellFormat}>
                  {i + 1}
                </Cell>
                <Cell
                  {...nameCell.getCellProps()}
                  cellFormat={nameCell.column.id as TCellFormat}
                  item={nameCell.row.original as ICellItem}
                >
                  {nameCell.render('Cell')}
                </Cell>
                {restColumns.map(renderDataCell)}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
});
